import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TreeNavigation } from "@/components/layout/TreeNavigation";
import MobileTabBar from "@/components/layout/MobileTabBar";
import { useResponsiveTables } from "@/components/shared/useResponsiveTables";
import { useAuth } from "@/contexts/AuthContext";
import { FiscalYearProvider, useFiscalYear } from "@/contexts/FiscalYearContext";
import { BreadcrumbProvider } from "@/contexts/BreadcrumbContext";
import { FISCAL_YEARS } from "@/constants/modules";
import { api } from "@/api";
import { LogOut, User, Menu, Bell, Calendar, Command, Moon, Sun } from "lucide-react";
import Breadcrumb from "@/components/shared/Breadcrumb";
import CommandPalette from "@/components/shared/CommandPalette";

function GlobalYearPicker() {
  const { year, setYear } = useFiscalYear();
  const selectRef = useRef(null);

  // Iter82 — Raccourci clavier "Shift+Y" (hors inputs/textarea/contenteditable)
  // pour ouvrir le dropdown annee fiscale. Desactivable via localStorage:
  //   localStorage.setItem('disable_year_shortcut', '1')
  useEffect(() => {
    if (typeof window === "undefined") return;
    const disabled = window.localStorage?.getItem("disable_year_shortcut") === "1";
    if (disabled) return;

    const handler = (e) => {
      // Trigger seulement si Shift+Y et qu'on n'est pas en train de taper
      if (e.key !== "Y" || !e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const isTyping =
        t && (
          t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable
        );
      if (isTyping) return;
      e.preventDefault();
      const el = selectRef.current;
      if (el) {
        el.focus();
        // Sur certains browsers, ouvrir le dropdown necessite cette astuce
        if (typeof el.showPicker === "function") {
          try { el.showPicker(); } catch { /* fallback to focus */ }
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div
      className="hidden md:flex items-center gap-1.5 bg-white/10 hover:bg-white/15 transition rounded-lg px-2 py-1"
      title="Annee fiscale active (raccourci : Shift + Y)"
    >
      <Calendar className="w-3.5 h-3.5 text-white/70" />
      <select
        ref={selectRef}
        value={year ?? ""}
        onChange={(e) => setYear(e.target.value ? parseInt(e.target.value) : null)}
        className="bg-transparent text-white text-xs border-0 focus:ring-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 cursor-pointer pr-1"
        data-testid="global-year-picker"
        aria-label="Annee fiscale globale (raccourci Shift+Y)"
      >
        <option value="" className="text-gray-900">Toutes annees</option>
        {FISCAL_YEARS.map((y) => (
          <option key={y} value={y} className="text-gray-900">{y}</option>
        ))}
      </select>
    </div>
  );
}

const MainLayoutInner = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [settings, setSettings] = useState({});
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mainRef = useRef(null);
  useResponsiveTables(mainRef);
  // Batch #14.231 — Mode sombre admin (mémorisé, n'affecte pas le portail client)
  const [theme, setTheme] = useState(() => localStorage.getItem("dg-admin-theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("dg-admin-theme", theme);
    return () => document.documentElement.classList.remove("dark");
  }, [theme]);

  // Iter83 — Command Palette : raccourci Cmd+K (Mac) / Ctrl+K (Windows/Linux).
  // Desactivable via localStorage.setItem('disable_command_palette', '1').
  useEffect(() => {
    if (typeof window === "undefined") return;
    const disabled = window.localStorage?.getItem("disable_command_palette") === "1";
    if (disabled) return;

    const handler = (e) => {
      if (e.key !== "k" && e.key !== "K") return;
      if (!(e.metaKey || e.ctrlKey)) return;
      // On laisse passer les raccourcis browser combines (ex: Cmd+Shift+K)
      if (e.altKey) return;
      e.preventDefault();
      setPaletteOpen((prev) => !prev);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const load = async () => { try { const r = await api.get("/notifications/unread-count"); setUnread(r.data.count); } catch { /* silent */ } };
    load();
    // Auto-refresh every 15s for near-real-time bell badge
    const interval = setInterval(load, 15000);
    // Also refresh when other parts of the app dispatch a notif:refresh event
    window.addEventListener("notif:refresh", load);
    // Refresh on tab focus
    window.addEventListener("focus", load);
    return () => {
      clearInterval(interval);
      window.removeEventListener("notif:refresh", load);
      window.removeEventListener("focus", load);
    };
  }, []);

  useEffect(() => {
    const loadSettings = async () => { try { const r = await api.get("/settings"); setSettings({ ...(r.data || {}), _logoTs: Date.now() }); } catch { /* silent */ } };
    loadSettings();
    window.addEventListener("settings:updated", loadSettings);
    return () => window.removeEventListener("settings:updated", loadSettings);
  }, []);

  const cDark = settings.primary_color || "#072354";
  const cMed = settings.secondary_color || "#2877D1";
  const cLight = settings.accent_color || "#00BFFF";
  const cSilver = settings.silver_color || "#C0C0C0";
  const logoUrl = settings.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}?t=${settings._logoTs || ''}` : null;

  const isPreview = window.location.hostname.includes("preview.emergentagent.com") || window.location.hostname === "localhost";

  return (
    <div className="flex h-screen bg-gray-50" data-testid="main-layout">
      <TreeNavigation mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} colors={{ cDark, cMed, cLight, cSilver }} logoUrl={logoUrl} companyName={settings.company_name} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {isPreview && (
          <div className="bg-amber-500 text-white text-center text-[10px] font-bold py-0.5 flex-shrink-0" data-testid="preview-banner">
            PREVIEW — Les donnees ici sont independantes de la production
          </div>
        )}
        <header className="h-12 flex items-center justify-between px-3 sm:px-4 flex-shrink-0 border-b" style={{ background: `linear-gradient(135deg, ${cDark}, ${cMed})` }}>
          <div className="flex items-center gap-2">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10" data-testid="open-sidebar-btn">
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-white hidden sm:block">DG Fiscal Flow</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setPaletteOpen(true)}
              className="hidden md:flex items-center gap-1.5 bg-white/10 hover:bg-white/15 transition rounded-lg px-2 py-1 text-white/80 hover:text-white"
              title="Palette de commandes (Cmd+K / Ctrl+K)"
              aria-label="Ouvrir la palette de commandes"
              data-testid="command-palette-trigger"
            >
              <Command className="w-3.5 h-3.5" />
              <span className="text-xs">Rechercher</span>
              <kbd className="text-[9px] px-1 rounded bg-white/15 font-mono ml-1">⌘K</kbd>
            </button>
            <GlobalYearPicker />
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-1.5 text-white/70 hover:text-white transition"
              title={theme === "dark" ? "Passer en mode clair" : "Passer en mode sombre"}
              data-testid="header-theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => navigate("/admin/notifications")} className="relative p-1.5 text-white/70 hover:text-white transition" data-testid="header-notif-btn">
              <Bell className="w-4 h-4" />
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">{unread > 9 ? "9+" : unread}</span>}
            </button>
            <button
              onClick={() => navigate("/admin/settings?tab=account")}
              className="text-xs text-white/80 hover:text-white hidden sm:flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition"
              title="Mon compte administrateur"
              data-testid="header-admin-account-btn"
            >
              <User className="w-3.5 h-3.5" />{user?.name || user?.email}
            </button>
            <button
              onClick={() => {
                // Scope-aware logout + SPA navigation (no hard reload → no blank page)
                logout();
                navigate("/", { replace: true });
              }}
              className="text-white/50 hover:text-red-300 transition p-1"
              data-testid="logout-btn"
              aria-label="Déconnexion"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        <main ref={mainRef} className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6 pb-mobile-nav">
          <Breadcrumb />
          {children}
        </main>
      </div>
      <MobileTabBar onSearch={() => setPaletteOpen(true)} onMenu={() => setSidebarOpen(true)} colors={{ cDark, cMed }} tabIds={settings.mobile_tabs} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};

export const MainLayout = ({ children }) => (
  <FiscalYearProvider>
    <BreadcrumbProvider>
      <MainLayoutInner>{children}</MainLayoutInner>
    </BreadcrumbProvider>
  </FiscalYearProvider>
);
